import { create } from 'zustand';

// Refresh token también vive en la cookie httpOnly que setea el backend;
// lo guardamos en memoria como fallback para flujos donde necesitamos
// el valor explícito (rotación). Sin localStorage por ADR-010.

export interface SessionUser {
  id: string;
  role: string;
  organizationId: string | null;
  email: string | null;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: SessionUser;
  // The backend's `sid` access-token claim (the server-side auth_sessions
  // row id) — HIGH fix (adversarial review, use-auth.ts finding #1): lets
  // the pending-logout flag target the EXACT session it meant to revoke
  // instead of "whatever session happens to be live when it flushes". `null`
  // when the token carries no `sid` (defensive — see token-signer.port.ts,
  // sid is optional there); optional so existing Session literals across the
  // codebase (tests, other call sites) do not need updating.
  sid?: string | null;
}

// Estado intermedio entre password OK y verificación MFA. Solo en memoria,
// no en cookie ni localStorage — si el usuario refresca pierde el flow y
// vuelve a /login. Aceptable porque el TTL del mfaToken es 5 min.
export interface MfaIntent {
  mfaToken: string;
  setupRequired: boolean;
  email: string;
}

// ── Pending logout (MEDIUM fix, adversarial review) ─────────────────────────
//
// A marker — no token, no full session data — so ADR-010's "no refresh
// token in client-readable storage" stays untouched. `localStorage` (not
// `sessionStorage`) so the intent survives a reload too: closing the tab
// mid-offline-signOut must not accidentally resurrect the old session on the
// NEXT launch before this flag gets a chance to flush.
//
// HIGH fix (use-auth.ts finding #1) — the flag now carries the `sid` of the
// session it targets (`null` when unknown, e.g. signOut() from an already-
// empty store). Lives here (not use-auth.ts, where it originally lived)
// because `setSession`/`authStore.set` — the ONLY two places a session is
// ever installed — must clear it the instant a NEW session lands, and
// use-auth.ts already depends on this module (the reverse import would be
// circular).
const PENDING_LOGOUT_STORAGE_KEY = 'jq4_pending_logout';

export interface PendingLogoutTarget {
  sid: string | null;
}

function hasLocalStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

export function markPendingLogout(sid: string | null): void {
  if (!hasLocalStorage()) return;
  try {
    const target: PendingLogoutTarget = { sid };
    localStorage.setItem(PENDING_LOGOUT_STORAGE_KEY, JSON.stringify(target));
  } catch {
    // Best-effort (e.g. a private-browsing quota rejection) — the next
    // signOut/refresh attempt tries the network call again regardless.
  }
}

export function clearPendingLogout(): void {
  if (!hasLocalStorage()) return;
  try {
    localStorage.removeItem(PENDING_LOGOUT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Full parsed target (sid it was armed for), or `null` if no flag is set. */
export function getPendingLogoutTarget(): PendingLogoutTarget | null {
  if (!hasLocalStorage()) return null;
  try {
    const raw = localStorage.getItem(PENDING_LOGOUT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'sid' in parsed) {
      const sid = (parsed as { sid: unknown }).sid;
      return { sid: typeof sid === 'string' ? sid : null };
    }
    // Legacy boolean-only flag ('1') from before this fix, or any other
    // unrecognized shape — treat as "pending, unknown target" so an
    // in-flight upgrade never silently drops a real intent.
    return { sid: null };
  } catch {
    return null;
  }
}

/** Exported for direct unit testing and for callers that need to introspect it. */
export function hasPendingLogout(): boolean {
  return getPendingLogoutTarget() !== null;
}

/** Why the last session ended, when the server told us something more specific than "expired". */
export type SessionExpiredReason = 'organization_suspended';

interface AuthState {
  session: Session | null;
  mfaIntent: MfaIntent | null;
  sessionExpired: boolean;
  /** QA-I11 — set only when the server rejected the refresh with a specific cause. */
  sessionExpiredReason: SessionExpiredReason | null;
  setSession: (session: Session | null) => void;
  setMfaIntent: (intent: MfaIntent | null) => void;
  expire: (reason?: SessionExpiredReason) => void;
  clearSessionExpired: () => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  mfaIntent: null,
  sessionExpired: false,
  sessionExpiredReason: null,
  setSession: (session) => {
    // HIGH fix (adversarial review, use-auth.ts finding #1) — ANY successful
    // login/session install (this is the ONE action every LoginPage across
    // both apps calls, via useAuth().setSession) must drop a stale
    // pending-logout flag. Without this, "sign out offline (flag set) → sign
    // in again before the flag flushes" left the flag armed, and the NEXT
    // refresh revoked the brand-new session instead of the one that was
    // actually signed out of. See `performRefresh` in use-auth.ts for the
    // second half of this fix (the sid-target check, for the same race when
    // this clear itself loses — e.g. a flush already in flight elsewhere).
    if (session) clearPendingLogout();
    set({ session, mfaIntent: null, sessionExpired: false, sessionExpiredReason: null });
  },
  setMfaIntent: (intent) => set({ mfaIntent: intent }),
  expire: (reason) =>
    set((state) => ({
      session: null,
      mfaIntent: null,
      // A suspension must be reported even when there was no in-memory session
      // (cold bootstrap after reload): the user still deserves to know why.
      sessionExpired: state.session !== null || state.sessionExpired || reason !== undefined,
      sessionExpiredReason: reason ?? null,
    })),
  clearSessionExpired: () => set({ sessionExpired: false, sessionExpiredReason: null }),
  signOut: () =>
    set({ session: null, mfaIntent: null, sessionExpired: false, sessionExpiredReason: null }),
}));

// Helpers para callers no-React (interceptor en client.ts, módulos con
// efectos colaterales, tests). Bajo el capó usan el mismo store de Zustand.
export const authStore = {
  get: (): Session | null => useAuthStore.getState().session,
  set: (session: Session | null): void => {
    // Same clear as the React `setSession` action above (HIGH fix,
    // use-auth.ts finding #1) — this is the non-React path `performRefresh`
    // uses to install a successfully-refreshed/rotated session.
    if (session) clearPendingLogout();
    useAuthStore.setState({
      session,
      mfaIntent: null,
      sessionExpired: false,
      sessionExpiredReason: null,
    });
  },
  getAccessToken: (): string | null => useAuthStore.getState().session?.accessToken ?? null,
  getRefreshToken: (): string | null => useAuthStore.getState().session?.refreshToken ?? null,
  expire: (reason?: SessionExpiredReason): void => useAuthStore.getState().expire(reason),
  signOut: (): void => useAuthStore.getState().signOut(),
  clearSessionExpired: (): void => useAuthStore.getState().clearSessionExpired(),
  subscribe: (fn: (session: Session | null) => void): (() => void) =>
    useAuthStore.subscribe((state) => fn(state.session)),
};

interface DecodedJwt {
  sub: string;
  org_id: string | null;
  role: string;
  exp: number;
  email?: string;
  // Server-side auth_sessions row id — see Session.sid above.
  sid?: string;
}

function decodeJwt(token: string): DecodedJwt | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as DecodedJwt;
  } catch {
    return null;
  }
}

export function sessionFromTokens(input: {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  email?: string | null;
}): Session | null {
  const claims = decodeJwt(input.accessToken);
  if (!claims) return null;
  return {
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    expiresAt: Date.now() + input.expiresIn * 1000,
    user: {
      id: claims.sub,
      role: claims.role,
      organizationId: claims.org_id,
      email: input.email ?? claims.email ?? null,
    },
    sid: claims.sid ?? null,
  };
}
